import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { requestsApi } from '../../lib/api';
import { differenceInHours, differenceInDays, parseISO } from 'date-fns';
import clsx from 'clsx';

interface SLARequest {
  id: number;
  reqId: string;
  title: string;
  priority: string;
  status: string;
  createdAt: string;
  firstResponseAt: string | null;
  startedAt: string | null;
}

function calculateSLAStatus(request: SLARequest) {
  const now = new Date();
  const createdAt = parseISO(request.createdAt);

  // SLA 기준: 응답 24시간, 착수 2일 (업무일 기준이지만 단순 계산)
  const hoursFromCreation = differenceInHours(now, createdAt);
  const daysFromCreation = differenceInDays(now, createdAt);

  const alerts: Array<{ type: 'response' | 'start'; severity: 'warning' | 'critical'; message: string }> = [];

  // 응답 SLA 체크 (OPEN 상태이고 firstResponseAt이 없는 경우)
  if (request.status === 'OPEN' && !request.firstResponseAt) {
    if (hoursFromCreation >= 24) {
      alerts.push({
        type: 'response',
        severity: 'critical',
        message: `응답 SLA 초과 (${hoursFromCreation}h 경과)`,
      });
    } else if (hoursFromCreation >= 20) {
      alerts.push({
        type: 'response',
        severity: 'warning',
        message: `응답 SLA 임박 (${24 - hoursFromCreation}h 남음)`,
      });
    }
  }

  // 착수 SLA 체크 (OPEN 상태이고 startedAt이 없는 경우)
  if ((request.status === 'OPEN' || request.status === 'IN_PROGRESS') && !request.startedAt) {
    if (daysFromCreation >= 2) {
      alerts.push({
        type: 'start',
        severity: 'critical',
        message: `착수 SLA 초과 (${daysFromCreation}일 경과)`,
      });
    } else if (daysFromCreation >= 1) {
      alerts.push({
        type: 'start',
        severity: 'warning',
        message: `착수 SLA 임박 (${2 - daysFromCreation}일 남음)`,
      });
    }
  }

  // P1 긴급은 더 엄격하게
  if (request.priority === 'P1') {
    if (request.status === 'OPEN' && !request.firstResponseAt && hoursFromCreation >= 4) {
      alerts.unshift({
        type: 'response',
        severity: 'critical',
        message: `[긴급] P1 요청 미응답 (${hoursFromCreation}h 경과)`,
      });
    }
  }

  return alerts;
}

export default function SLAAlert() {
  const { data, isLoading } = useQuery({
    queryKey: ['requests', 'sla-check'],
    queryFn: () => requestsApi.list({ status: 'OPEN,IN_PROGRESS', pageSize: '50' }),
    refetchInterval: 60000, // 1분마다 갱신
  });

  const requests = (data?.data?.items || []) as SLARequest[];

  const alertRequests = requests
    .map((req) => ({
      ...req,
      alerts: calculateSLAStatus(req),
    }))
    .filter((req) => req.alerts.length > 0)
    .sort((a, b) => {
      // Critical 먼저, 그 다음 P1 우선
      const aHasCritical = a.alerts.some((al) => al.severity === 'critical');
      const bHasCritical = b.alerts.some((al) => al.severity === 'critical');
      if (aHasCritical !== bHasCritical) return bHasCritical ? 1 : -1;
      if (a.priority !== b.priority) return a.priority.localeCompare(b.priority);
      return 0;
    });

  if (isLoading) {
    return null;
  }

  if (alertRequests.length === 0) {
    return null;
  }

  return (
    <div className="card border-l-4 border-l-red-500">
      <div className="p-4 border-b border-gray-100 bg-red-50">
        <h3 className="font-semibold text-red-700 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          SLA 경고 ({alertRequests.length}건)
        </h3>
      </div>
      <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
        {alertRequests.map((req) => (
          <Link
            key={req.id}
            to={`/requests/${req.id}`}
            className="block p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={clsx(
                    'px-1.5 py-0.5 text-xs font-medium rounded',
                    req.priority === 'P1' && 'bg-red-100 text-red-700',
                    req.priority === 'P2' && 'bg-yellow-100 text-yellow-700',
                    req.priority === 'P3' && 'bg-green-100 text-green-700',
                  )}>
                    {req.priority}
                  </span>
                  <span className="text-sm text-gray-500">{req.reqId}</span>
                </div>
                <p className="text-sm font-medium text-gray-900 truncate mt-1">
                  {req.title}
                </p>
              </div>
            </div>
            <div className="mt-2 space-y-1">
              {req.alerts.map((alert, idx) => (
                <p
                  key={idx}
                  className={clsx(
                    'text-xs',
                    alert.severity === 'critical' ? 'text-red-600 font-medium' : 'text-yellow-600'
                  )}
                >
                  {alert.severity === 'critical' ? '⚠️' : '⏰'} {alert.message}
                </p>
              ))}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
